import { useEffect, useRef, useState } from 'react';
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Button, Card, CardBody, CardHeader, Heading, Image, SimpleGrid, Stack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { FiEdit3, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { AppError } from '../../utils/AppError';
import Loading from '../../components/Loading';
import Sidebar from '../../components/Sidebar';
import ModalConta from '../../components/ComponentsConta/ModalConta';

import { ButtonAddItemContent, Container, Content, Header } from './styles';

type ContaDATA = {
	id_emp: string;
	id_caixa: number;
	url_banco: string;
	conta_tipo: string;
	conta_desc: string;
	banco_cod: string;
	banco_agencia: string;
	banco_agencia_dig: string;
	banco_conta_nro: string;
	banco_conta_dig: string;
	limite_credito: number;
	cartao_conta_deb_nro: string;
	cartao_conta_deb_dig: string;
	cartao_nro: string;
	cartao_nome: string;
	cartao_mes_validade: string;
	cartao_ano_validade: string;
	cartao_dia_vencimento: number;
	cartao_melhor_dia_compra: number;
	boleto: {
		documento: string;
		nome_boleto: string;
		cedente: string;
		carteira: string;
		variacao: string;
		codtrans240: string;
		codtrans400: string;
		tip_dist: number;
		tip_arq: number;
		tip_carteira: number;
		especie: string;
		rep_emissao: string;
		aceite: string;
	};
	pix: {
		client_id: string;
		client_secret: string;
		key_user: string;
		chave_pix: string;
		ssl_lib: string;
	};
	users: string[];
}

const Contas: React.FC = () => {
	const { company, permissions } = useAuth();
	const { addToast } = useToast();

	const [loading, setLoading] = useState(true);

	const [contas, setContas] = useState<ContaDATA[]>([]);
	const [contaModal, setContaModal] = useState<ContaDATA>({} as ContaDATA)

	const [modalContaVisible, setModalContaVisible] = useState(false);
	const [usuarios, setUsuarios] = useState([]);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = useRef(null);

	async function fetchContas () {
		try {
			setLoading(true);
			const { data } = await api.get('/portal/conta')
				.catch((err) => {
					throw err;
				});
			
			setContas(data.contas);
			setUsuarios(data.usuarios);

		} catch (error) {
			console.log('error ==> ', error);

			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Não foi possível buscar as contas.';

			addToast({
				type: 'error',
				title: 'Buscar Contas!',
				description: msg,
			});

		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetch = async () => await fetchContas();
		fetch();
	}, [company]);

	async function handleFetchConta(caixaId: number) {
		try {
			setLoading(true);

			const { data } = await api.get(`/portal/conta/${caixaId}`);

			setContaModal({ ...data });
			setModalContaVisible(true);
		}
		catch (error) {
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Não foi possível buscar a conta.';

			addToast({
				type: 'error',
				title: 'Buscar Conta!',
				description: msg,
			});
		}
		finally {
			setLoading(false);
		}
	};

	async function handleDeleteConta(caixaId: number) {
		try {
			setLoading(true);

			const { data } = await api.delete(`/portal/conta/${caixaId}`);

			if (data.che === 'S') {
				addToast({
					type: 'success',
					title: 'Conta Deletada!',
					description: data.msg,
				});
			} else {
				addToast({
					type: 'error',
					title: 'Deletar Conta!',
					description: data.msg,
				});
			}
		}
		catch (error) {
			const isAppError = error instanceof AppError;
			const msg = isAppError ? error.message : 'Não foi possível deletar a conta.';

			addToast({
				type: 'error',
				title: 'Deletar Conta!',
				description: msg,
			});
		}
		finally {
			onClose();
			fetchContas();
			setLoading(false);
		}
	};

	return (
		<>
			<Container>
				<Sidebar />

				{loading && <Loading />}

				<Content>
					<Header>
						<div className='header-texts'>
							<h1>Contas.</h1>
							<h2>Cadastre ou altere suas contas.</h2>
						</div>

						{permissions.incluir && (
						<ButtonAddItemContent
							onClick={() => {
								setContaModal({} as ContaDATA);
								setModalContaVisible(true);
							}}
						>
							+ Novo
						</ButtonAddItemContent>
					) }
					</Header>

					{contas.length === 0 ? (
						<Text fontSize={22}>Nenhuma conta cadastrada para esta empresa.</Text>
					) : (
						<SimpleGrid spacing={4} templateColumns='repeat(auto-fill, minmax(400px, 1fr))'
							w="100%" alignItems="flex-start"
						>
							{contas.map(conta => {
								return (
									<Card key={conta.id_caixa} variant="elevated" borderWidth={1} borderColor="gray.100">
										<CardHeader py={1}>
											<Stack direction="row" justify="space-between" alignItems="center">
												<Heading size='md'>
													{conta.url_banco
														? <Image w="30" h="25" src={conta.url_banco} alt={conta.conta_tipo} />
														: <Text>--</Text>
													}
												</Heading>

												<Button
													p={1}
													h={7}
													bg="transparent"
													color='blue.500'
													_hover={{ bg: "blue.50" }}
													onClick={() => handleFetchConta(conta.id_caixa)}
												>
													<FiEdit3 />&nbsp;Editar
												</Button>
											</Stack>
										</CardHeader>

										<CardBody py={1}>
											<Stack direction='row' justify="space-between">
												<VStack alignItems="flex-start">
													<Text fontWeight={500}>{conta.conta_desc}</Text>
													<Text fontWeight={500}>tipo: {conta.conta_tipo === 'B' ? 'Banco' : conta.conta_tipo === 'CR' ? 'Cartão' : 'Caixa'}</Text>
												</VStack>

												<VStack justifyContent="flex-end">
													<Button
														p={1}
														h={7}
														bg="transparent"
														color='red.500'
														_hover={{ bg: "red.50" }}
														onClick={() => {
															setContaModal(conta);
															onOpen();
														}}
													>
														<FiTrash2 />&nbsp;Excluir
													</Button>
												</VStack>
											</Stack>
										</CardBody>
									</Card>
								)
							})}

						</SimpleGrid>
					)}
					
				</Content>
			</Container>

			{modalContaVisible && (
				<ModalConta
					isVisible={modalContaVisible}
					onClose={() => setModalContaVisible(false)}
					conta={contaModal}
					fetchContas={fetchContas}
					usuarios={usuarios}
				/>
			)}

			<AlertDialog
				isOpen={isOpen}
				leastDestructiveRef={cancelRef}
				onClose={onClose}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize='lg' fontWeight='bold'>
							Delete Conta
						</AlertDialogHeader>

						<AlertDialogBody>
							<Text>Tem certeza que deseja excluir esta conta?</Text>

							<VStack
								p={2}
								mt={2}
								spacing={1}
								alignItems="flex-start"
								borderWidth={1}
								borderColor="gray.100"
								rounded={8}
							>
								{contaModal.url_banco
									? <Image w="30" h="25" src={contaModal.url_banco} alt={contaModal.conta_tipo} />
									: <Text>--</Text>
								}
								<Text fontWeight={500}>{contaModal.conta_desc}</Text>
								<Text>tipo: {contaModal.conta_tipo}</Text>
							</VStack>
						</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onClose}>
								Cancel
							</Button>

							<Button
								colorScheme='red'
								ml={3}
								onClick={() => handleDeleteConta(contaModal.id_caixa)}
							>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	)
}

export default Contas;